import { Injectable } from "@angular/core";
import { fromBER } from "asn1js";
import { ContentInfo, SignedData } from "pkijs";
import PostalMime from "postal-mime";

@Injectable({ providedIn: "root" })
export class SmimeUnpackService {
  async unpackEml(emlText: string): Promise<{ name: string; contentType: string; content: string }[]> {
    const mimeBytes = new TextEncoder().encode(emlText);
    const parser = new PostalMime();
    const email = await parser.parse(mimeBytes);
    return email.attachments.map(att => ({
      name: att.filename ?? "attachment",
      contentType: att.mimeType ?? "application/octet-stream",
      content: this.toBase64(att.content),
    }));
  }

  async unpack(base64: string): Promise<{ name: string; contentType: string; content: string }[]> {
    const buffer = this.base64ToArrayBuffer(base64);
    const bytes = new Uint8Array(buffer);

    let mimeBytes: Uint8Array;

    if (bytes[0] === 0x30) {
      // DER-encoded CMS — unwrap SignedData to get inner MIME message
      let asn1: ReturnType<typeof fromBER>;
      try {
        asn1 = fromBER(buffer);
      } catch (e) {
        throw new Error(`fromBER threw: ${e?.message ?? JSON.stringify(e)}`);
      }
      if (asn1.offset === -1) {
        throw new Error("fromBER returned offset -1 — not valid DER/BER");
      }

      let contentInfo: ContentInfo;
      try {
        contentInfo = new ContentInfo({ schema: asn1.result });
      } catch (e) {
        throw new Error(`ContentInfo parse failed: ${e?.message ?? JSON.stringify(e)}`);
      }

      let signedData: SignedData;
      try {
        signedData = new SignedData({ schema: contentInfo.content });
      } catch (e) {
        throw new Error(`SignedData parse failed: ${e?.message ?? JSON.stringify(e)}`);
      }

      const eContent = signedData.encapContentInfo?.eContent;
      if (!eContent) {
        throw new Error("No eContent in SignedData — may be detached signature");
      }

      mimeBytes = this.extractOctetStringBytes(eContent);
    } else {
      // Raw MIME message — Graph API returned the full email, skip CMS layer
      mimeBytes = bytes;
    }

    const parser = new PostalMime();
    const email = await parser.parse(mimeBytes);

    return email.attachments.map(att => ({
      name: att.filename ?? "attachment",
      contentType: att.mimeType ?? "application/octet-stream",
      content: this.toBase64(att.content),
    }));
  }

  private extractOctetStringBytes(octetString: any): Uint8Array {
    const block = octetString.valueBlock;
    if (block.isConstructed && Array.isArray(block.value)) {
      const chunks: Uint8Array[] = block.value.map((chunk: any) =>
        new Uint8Array(chunk.valueBlock.valueHexView)
      );
      const total = chunks.reduce((sum, c) => sum + c.length, 0);
      const result = new Uint8Array(total);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
      }
      return result;
    }
    return new Uint8Array(block.valueHexView);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  private toBase64(content: Uint8Array | ArrayBuffer | string): string {
    if (typeof content === "string") return btoa(content);
    const bytes = content instanceof ArrayBuffer ? new Uint8Array(content) : content;
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}
